// Teachers CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type {
  TeacherWithRelations,
  CreateTeacherRequest,
  UpdateTeacherRequest,
  TeacherFilters,
  AssignSubjectsToTeacherRequest,
} from '@timetable/types'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}


// Get all teachers with filters
export async function getTeachersDirect(filters: TeacherFilters = {}): Promise<TeacherWithRelations[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Build query parameters
    const params = new URLSearchParams()
    params.append('select', '*,teacher_type:teacher_types(*),teacher_subjects(subject:subjects(*))')
    
    // Apply school context filter (unless Super Admin without context)
    const schoolContext = getSchoolContext()
    const schoolId = filters.school_id || schoolContext
    
    if (schoolId) {
      params.append('school_id', `eq.${schoolId}`)
    }
    if (filters.teacher_type_id) {
      params.append('teacher_type_id', `eq.${filters.teacher_type_id}`)
    }
    if (filters.search) {
      params.append('name', `ilike.*${filters.search}*`)
    }
    if (!filters.includeInactive) {
      params.append('is_active', 'eq.true')
    }
    params.append('order', 'name.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch teachers')
    }

    const data: any[] = await response.json()

    // Transform the data to match TeacherWithRelations
    return data.map((teacher: any) => ({
      ...teacher,
      subjects: teacher.teacher_subjects?.map((ts: any) => ts.subject) || [],
    }))
  })
}

// Get a single teacher by ID
export async function getTeacherByIdDirect(id: string): Promise<TeacherWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*,teacher_type:teacher_types(*),teacher_subjects(subject:subjects(*))')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Teacher not found')
    }

    const data: any = await response.json()

    return {
      ...data,
      subjects: data.teacher_subjects?.map((ts: any) => ts.subject) || [],
    }
  })
}

// Create a new teacher
export async function createTeacherDirect(teacher: CreateTeacherRequest): Promise<TeacherWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(teacher),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create teacher')
    }

    const data: any[] = await response.json()
    const created = data[0]

    // Fetch with relations
    return getTeacherByIdDirect(created.id)
  })
}

// Update a teacher
export async function updateTeacherDirect(
  id: string,
  updates: UpdateTeacherRequest
): Promise<TeacherWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update teacher')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Teacher not found')

    // Fetch with relations
    return getTeacherByIdDirect(id)
  })
}

// Check if a teacher has any timetable records (used for soft vs hard delete decision)
export async function hasTeacherTimetableRecords(id: string): Promise<boolean> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const checkResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?teacher_id=eq.${id}&select=id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )
    return checkResp.ok && ((await checkResp.json()) as any[]).length > 0
  })
}

// Delete a teacher: soft-delete if records exist, hard-delete otherwise
export async function deleteTeacherDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check for existing timetable records
    const checkResp = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?teacher_id=eq.${id}&select=id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    const hasTimetableRecords = checkResp.ok && ((await checkResp.json()) as any[]).length > 0

    if (hasTimetableRecords) {
      // Soft-delete: set is_active = false and deactivated_at
      const softResp = await fetch(`${SUPABASE_URL}/rest/v1/teachers?id=eq.${id}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: false, deactivated_at: new Date().toISOString() }),
      })
      if (!softResp.ok) {
        const error = await softResp.json()
        throw new Error(error.message || 'Failed to deactivate teacher')
      }
    } else {
      // Hard-delete
      const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?id=eq.${id}`, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      })
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to delete teacher')
      }
    }
  })
}

// Reactivate a soft-deleted teacher
export async function reactivateTeacherDirect(id: string): Promise<TeacherWithRelations> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teachers?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ is_active: true }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to reactivate teacher')
    }

    return getTeacherByIdDirect(id)
  })
}

// Assign subjects to a teacher
export async function assignSubjectsToTeacherDirect(
  request: AssignSubjectsToTeacherRequest
): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // First, delete existing assignments
    await fetch(`${SUPABASE_URL}/rest/v1/teacher_subjects?teacher_id=eq.${request.teacher_id}`, {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    // Then, create new assignments
    if (request.subject_ids.length > 0) {
      const assignments = request.subject_ids.map((subject_id) => ({
        teacher_id: request.teacher_id,
        subject_id,
      }))

      const response = await fetch(`${SUPABASE_URL}/rest/v1/teacher_subjects`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assignments),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to assign subjects')
      }
    }
  })
}

// Get teacher's assigned subjects
export async function getTeacherSubjectsDirect(teacherId: string) {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', 'subject:subjects(*)')
    params.append('teacher_id', `eq.${teacherId}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/teacher_subjects?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch teacher subjects')
    }

    const data: any[] = await response.json()
    return data.map((ts: any) => ts.subject)
  })
}
